import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const StickyCallButton = () => {
  const fixSteps = [
    { step: "Choose button placement (bottom right or bottom center)", details: "Bottom right is standard; bottom center is more prominent but takes more screen space" },
    { step: "Use a phone icon with contrasting color", details: "Green or your brand's primary color works well—make it unmissable" },
    { step: "Make the button large enough to tap easily", details: "At least 44x44 pixels (Apple's minimum), ideally 56-60 pixels" },
    { step: "Link directly to tel:your-phone-number", details: "Use href='tel:+1234567890' format for instant one-tap calling" },
    { step: "Add subtle animation to draw attention", details: "A gentle pulse or bounce every few seconds catches the eye without annoying" },
    { step: "Show only on mobile devices", details: "Desktop users can't tap-to-call, so hide on larger screens or show a form CTA instead" },
    { step: "Test on multiple devices", details: "Ensure the button doesn't overlap important content or navigation" }
  ];

  const codeExample = `<!-- Simple Sticky Call Button CSS -->
<style>
.sticky-call-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background-color: #22c55e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  z-index: 999;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@media (min-width: 768px) {
  .sticky-call-btn { display: none; }
}
</style>

<!-- HTML -->
<a href="tel:+11234567890" class="sticky-call-btn">
  <svg><!-- Phone icon --></svg>
</a>`;

  const faqs = [
    { question: "What is a sticky call button?", answer: "A sticky call button is a floating phone icon that stays visible as visitors scroll on mobile devices. Tapping it immediately initiates a phone call to your business, making it effortless for mobile visitors to contact you." },
    { question: "Do sticky call buttons increase leads?", answer: "Yes, significantly. Websites with sticky call buttons see 20-40% more phone calls from mobile visitors. The button removes friction—instead of scrolling to find your number, visitors can call with one tap at any moment." },
    { question: "Where should I place a sticky call button?", answer: "Bottom right corner is the most common placement. It's within thumb reach on most phones and doesn't interfere with typical scrolling. Bottom center is more prominent but takes up more visual space." },
    { question: "Should I show the sticky button on desktop?", answer: "No. Desktop users can't tap-to-call, so the button serves no purpose. Either hide it on larger screens or replace it with a 'Get Quote' button that opens your form or scrolls to your contact section." },
    { question: "What color should my call button be?", answer: "Use a high-contrast color that stands out from your site. Green is commonly associated with 'call' buttons (like phone apps). Your brand's primary accent color also works if it's visible against your background." },
    { question: "How big should the call button be?", answer: "At minimum, 44x44 pixels (Apple's recommended tap target). Ideally, 56-60 pixels for easier tapping. Too small and visitors will miss it; too large and it becomes intrusive." },
    { question: "Should the button have animation?", answer: "Subtle animation (a gentle pulse every few seconds) draws attention without being annoying. Avoid aggressive flashing or constant movement, which can frustrate visitors and feel spammy." },
    { question: "Can I add a sticky call button to any website?", answer: "Yes. With basic CSS and HTML, you can add a sticky call button to WordPress, Wix, Squarespace, or any custom site. Most website builders also have plugins or built-in features for this." },
    { question: "Should I track calls from the sticky button?", answer: "Absolutely. Use call tracking (a unique phone number for the button) to measure exactly how many calls it generates. This helps you understand the button's ROI and optimize placement." },
    { question: "Will a sticky button slow down my website?", answer: "Not noticeably. A properly coded sticky button adds minimal file size. Avoid bloated plugins when a simple CSS/HTML solution works just as well." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/lawn-care-website-design/", label: "Lawn Care Website Design" },
    { to: "/website-fixes/lawn-care-website-not-getting-leads/", label: "Website Not Getting Leads" },
    { to: "/comparisons/call-tracking-tools-for-lawn-care-websites/", label: "Call Tracking Tools" }
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/website-fixes/sticky-call-button/";

  return (
    <>
      <SEO
        title="How to Add a Sticky Call Button to Your Website"
        description="Learn how to add a sticky call button to your service business website. Step-by-step guide with code examples to increase mobile conversions."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema
        url={CANONICAL}
        title="How to Add a Sticky Call Button to Your Website"
        description="Learn how to add a sticky call button to your service business website with code examples."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              How to Add a Sticky Call Button to Your Website
            </h1>
            <p className="text-xl text-muted-foreground">
              Make it effortless for mobile visitors to call you. A sticky call button can increase phone leads by 20-40%.
            </p>
          </header>

          <TopCTA />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Why Every Service Website Needs a Sticky Call Button</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Over 60% of lawn care and landscaping searches happen on mobile devices. When someone finds your website on their phone, they're often ready to call immediately—if they can find your number.
              </p>
              <p>
                A sticky call button solves this by keeping a tap-to-call option visible at all times. No scrolling to the footer, no hunting for the contact page. <strong>One tap, instant connection.</strong>
              </p>
              <p>
                Websites with sticky call buttons typically see 20-40% more phone calls from mobile visitors. For a lawn care business getting 500 mobile visitors per month, that could mean 10-20 extra phone calls monthly.
              </p>
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Add a Sticky Call Button" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Simple Code Example</h2>
            <div className="bg-muted p-6 rounded-lg overflow-x-auto">
              <pre className="text-sm text-foreground whitespace-pre-wrap">{codeExample}</pre>
            </div>
            <p className="text-muted-foreground mt-4">
              This basic code creates a green, pulsing call button in the bottom right corner that only appears on mobile devices. Customize colors and positioning to match your brand.
            </p>
          </section>

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Best Practices for Maximum Conversions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-muted rounded-lg">
                <h3 className="font-bold text-foreground mb-2">✅ Do This</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Use a recognizable phone icon</li>
                  <li>• Choose a contrasting, visible color</li>
                  <li>• Make it large enough to tap easily</li>
                  <li>• Add subtle animation</li>
                  <li>• Track calls separately for measurement</li>
                </ul>
              </div>
              <div className="p-6 bg-muted rounded-lg">
                <h3 className="font-bold text-foreground mb-2">❌ Avoid This</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Buttons that blend into the background</li>
                  <li>• Placing it over important content</li>
                  <li>• Aggressive, constant animation</li>
                  <li>• Showing on desktop (where tap-to-call doesn't work)</li>
                  <li>• Making it too small to tap accurately</li>
                </ul>
              </div>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get a Conversion-Optimized Website" />
        </div>
      </article>
    </>
  );
};

export default StickyCallButton;
